import React from 'react';
import { Skeleton } from './Skeleton';

/**
 * KPICard — UI-02 ek: Dashboard üstünde küçük özet kartları.
 *
 * Props:
 *   icon      : emoji / karakter
 *   label     : üst satır etiketi
 *   value     : büyük sayı (null/undefined → "—")
 *   subLabel  : alt satır (örn. "bugün" veya "canlı")
 *   accent    : 'blue' | 'green' | 'amber' | 'red' | 'indigo'
 *   loading   : iskelet göster
 *   onClick   : opsiyonel navigate
 *   spark     : opsiyonel sayı dizisi — inline mini sparkline
 */
const ACCENTS = {
  blue:   { text: 'text-blue-600 dark:text-blue-400',     stroke: '#2563eb' },
  green:  { text: 'text-emerald-600 dark:text-emerald-400', stroke: '#10b981' },
  amber:  { text: 'text-amber-600 dark:text-amber-400',   stroke: '#f59e0b' },
  red:    { text: 'text-red-600 dark:text-red-400',       stroke: '#ef4444' },
  indigo: { text: 'text-indigo-600 dark:text-indigo-400', stroke: '#6366f1' },
};

function MiniSpark({ data, stroke }) {
  if (!Array.isArray(data) || data.length < 2) return null;
  const w = 120, h = 24, pad = 2;
  const peak = Math.max(...data, 1);
  const stepX = (w - pad * 2) / (data.length - 1);
  const points = data
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = h - pad - (v / peak) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-6 mt-2" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export default function KPICard({
  icon,
  label,
  value,
  subLabel,
  accent = 'blue',
  loading = false,
  onClick,
  spark,
}) {
  const a = ACCENTS[accent] || ACCENTS.blue;
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      className={`bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 ${
        interactive ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</div>
          {loading ? (
            <Skeleton className="h-7 w-20 mt-1.5" />
          ) : (
            <div className={`text-2xl font-bold mt-1 ${a.text}`}>
              {value === null || value === undefined || value === '' ? '—' : value}
            </div>
          )}
          {subLabel && (
            <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mt-0.5">
              {subLabel}
            </div>
          )}
        </div>
        <div className="text-2xl flex-shrink-0" aria-hidden="true">{icon}</div>
      </div>
      {!loading && spark && <MiniSpark data={spark} stroke={a.stroke} />}
    </div>
  );
}
